/*
 * ──────────────────────────────────────────────────────────────
 *  OCTA CORE — Event Taxonomy (Mixpanel)
 * ──────────────────────────────────────────────────────────────
 *
 *  ACQUISITION
 *    Signup Completed          – first-time user identified
 *    Login                     – returning session start
 *    Landing Page Viewed       – choose-plan page load
 *    Plan Selected             – user picks a plan
 *    Trial Started             – trial begins
 *    Checkout Started          – Stripe checkout initiated
 *
 *  ACTIVATION
 *    Onboarding *              – all onboarding step events
 *    First Invoice Created     – milestone
 *    First Bill Created        – milestone
 *    First Sync Completed      – first successful accounting sync
 *    First Customer Created    – first customer added
 *    First Vendor Created      – first vendor added
 *    First Automation Created  – first automation post-onboarding
 *    Setup Checklist Completed – all getting-started steps done
 *
 *  ENGAGEMENT
 *    Session Started           – app load, with session_number
 *    Feature Module Visited    – first nav to a module per session
 *    AI Chat First Query       – first AI chat per workspace
 *    Weekly Active             – last_active_date super prop
 *    (page views, dashboard events, etc.)
 *
 *  REVENUE
 *    Checkout Started          – (existing)
 *    Billing Portal Opened     – (existing)
 *    Credit Purchase Started   – (existing)
 *    Upgrade Started           – (existing)
 *    Subscription Activated    – active subscription detected
 *    Trial Expired             – trial → starter downgrade detected
 *    Upgrade Completed         – upgrade-welcome page shown
 *    Downgrade Detected        – downgrade-notice page rendered
 *
 *  RETENTION
 *    Returning User            – last visit > 24 h ago
 *    Dormant User Returned     – last visit > 7 d ago
 *    Workspace Health Check    – dashboard load with health props
 *    Feature Depth Score       – # distinct modules used (profile)
 *
 *  All new events include `tier` and `workspace_id` via super
 *  properties registered on identify.
 * ──────────────────────────────────────────────────────────────
 */

interface MixpanelPeople {
  set: (props: Record<string, unknown>) => void;
  set_once: (props: Record<string, unknown>) => void;
  increment: (prop: string, by?: number) => void;
}
interface MixpanelLike {
  identify: (id: string) => void;
  register: (props: Record<string, unknown>) => void;
  reset: () => void;
  track: (name: string, properties?: Record<string, unknown>) => void;
  track_pageview: (props?: Record<string, unknown>) => void;
  people: MixpanelPeople;
}

declare global {
  interface Window {
    mixpanel?: MixpanelLike;
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function mp(): MixpanelLike | undefined {
  return window.mixpanel;
}

function ga(...args: unknown[]) {
  if (typeof window.gtag === "function") {
    window.gtag(...args);
  }
}

let _currentWorkspaceId = "";

/**
 * Shared identifyUser() — OCTA Core + OCTA Flow attribution contract.
 *
 * Both apps MUST call identifyUser(user, platform) on first authenticated
 * session. Acquisition trail is sourced from the `octa_utm` cookie set
 * by the marketing site on `.weareocta.com`. Missing cookie ⇒ defaults
 * (utm_source="direct", utm_medium="none", utm_campaign="none",
 * landing_page="/") are still used for analytics identification, but the
 * workspace POST is only fired for first-time signups so existing
 * accounts can never be retroactively reclassified.
 *
 * Cookie shape (URL-encoded JSON):
 *   { utm_source, utm_medium, utm_campaign, landing_page, first_touch }
 *
 * POST /api/users/attribution payload:
 *   { userId, platform, utm_source, utm_medium, utm_campaign,
 *     landing_page, first_touch }
 * Server is first-touch-wins per column (COALESCE/IS NULL).
 */

export type AnalyticsPlatform = "core" | "flow";

/**
 * Per-field attribution. Each field is either the real value sourced
 * from the `octa_utm` cookie, or null if that field was absent. We
 * deliberately do NOT carry "direct/none" defaults inside this struct
 * — defaults are only applied at the analytics-identification call
 * sites (Mixpanel/GA4) so the wire payload to `/api/users/attribution`
 * stays free of synthetic data.
 */
export interface AttributionData {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  landing_page: string | null;
  first_touch: string | null;
}

const ATTRIBUTION_COOKIE = "octa_utm";
const ATTRIBUTION_POSTED_KEY_BASE = "octa-attribution-posted";

const ATTRIBUTION_DEFAULTS = {
  utm_source: "direct",
  utm_medium: "none",
  utm_campaign: "none",
  landing_page: "/",
} as const;

const EMPTY_ATTRIBUTION: AttributionData = {
  utm_source: null,
  utm_medium: null,
  utm_campaign: null,
  landing_page: null,
  first_touch: null,
};

function readAttributionCookie(): AttributionData {
  try {
    if (typeof document === "undefined") return EMPTY_ATTRIBUTION;
    const match = document.cookie
      .split(";")
      .map((s) => s.trim())
      .find((s) => s.startsWith(ATTRIBUTION_COOKIE + "="));
    if (!match) return EMPTY_ATTRIBUTION;
    const raw = decodeURIComponent(match.slice(ATTRIBUTION_COOKIE.length + 1));
    if (!raw) return EMPTY_ATTRIBUTION;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const pick = (k: string): string | null =>
      typeof parsed[k] === "string" && (parsed[k] as string) ? (parsed[k] as string) : null;
    let first_touch: string | null = null;
    const ft = parsed.first_touch;
    if (typeof ft === "string" && ft) {
      first_touch = ft;
    } else if (typeof ft === "number" && Number.isFinite(ft)) {
      const d = new Date(ft);
      if (!isNaN(d.getTime())) first_touch = d.toISOString();
    }
    return {
      utm_source: pick("utm_source"),
      utm_medium: pick("utm_medium"),
      utm_campaign: pick("utm_campaign"),
      landing_page: pick("landing_page"),
      first_touch,
    };
  } catch {
    return EMPTY_ATTRIBUTION;
  }
}

const GA_ID_BY_PLATFORM: Record<AnalyticsPlatform, string> = {
  core: "G-CE5RLFB4M0",
  flow: "G-QXC658PD81",
};

function getCsrfTokenFromCookie(): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.split(";").map((s) => s.trim()).find((s) => s.startsWith("csrf_token="));
  return match ? decodeURIComponent(match.slice("csrf_token=".length)) : undefined;
}

function postAttributionOnce(
  userId: string,
  workspaceId: string,
  platform: AnalyticsPlatform,
  attribution: AttributionData,
): void {
  const flagKey = `${ATTRIBUTION_POSTED_KEY_BASE}:${workspaceId}`;
  try {
    if (localStorage.getItem(flagKey)) return;
  } catch {}

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const csrf = getCsrfTokenFromCookie();
  if (csrf) headers["x-csrf-token"] = csrf;

  fetch("/api/users/attribution", {
    method: "POST",
    credentials: "include",
    headers,
    // Per-field nullability: each missing cookie field goes to the
    // server as null, never as a synthetic "direct/none" default. The
    // server uses COALESCE so partial signals (e.g. only landing_page)
    // still persist their real value without polluting other columns.
    body: JSON.stringify({
      userId,
      platform,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      landing_page: attribution.landing_page,
      first_touch: attribution.first_touch,
    }),
  })
    .then((res) => {
      if (res.ok) {
        try {
          localStorage.setItem(flagKey, "1");
        } catch {}
      }
    })
    .catch(() => {});
}

export function identifyUser(
  user: {
    id: string;
    email: string;
    fullName: string;
    workspaceId: string;
    role: string;
    tier?: string;
    firstLoginPending?: boolean;
  },
  platform: AnalyticsPlatform = "core",
) {
  _currentWorkspaceId = user.workspaceId;

  const attribution = readAttributionCookie();
  const cachedTier = (typeof localStorage !== "undefined" && localStorage.getItem("octa-cached-tier")) || "";
  const plan = user.tier || cachedTier || "unknown";

  // Per-field defaults for analytics dashboards only — never sent to
  // the server. This keeps Mixpanel/GA4 segmentation clean while the
  // workspace POST stays free of synthetic data.
  const utmSourceForAnalytics = attribution.utm_source ?? ATTRIBUTION_DEFAULTS.utm_source;
  const utmMediumForAnalytics = attribution.utm_medium ?? ATTRIBUTION_DEFAULTS.utm_medium;
  const utmCampaignForAnalytics = attribution.utm_campaign ?? ATTRIBUTION_DEFAULTS.utm_campaign;
  const landingPageForAnalytics = attribution.landing_page ?? ATTRIBUTION_DEFAULTS.landing_page;

  // GA4: stitch Core + Flow under one user_id via cross-subdomain cookie.
  ga("config", GA_ID_BY_PLATFORM[platform], {
    user_id: user.id,
    cookie_domain: ".weareocta.com",
    cookie_flags: "SameSite=Lax",
    send_page_view: false,
  });
  ga("set", "user_properties", { platform, plan });

  // GTM dataLayer: surface user_id (UUID, never PII) to GTM-managed tags
  // (Facebook Pixel, LinkedIn Insight, etc.) which listen to dataLayer.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ user_id: user.id });

  // Mixpanel: identify and set merged profile. Acquisition fields use
  // defaults when no cookie is present so dashboards never have null
  // breakdowns; the workspace POST below is what enforces first-touch.
  const m = mp();
  if (m) {
    m.identify(user.id);
    m.people.set({
      $email: user.email,
      $name: user.fullName,
      workspace_id: user.workspaceId,
      role: user.role,
      platform,
      plan,
      acquisition_source: utmSourceForAnalytics,
      acquisition_medium: utmMediumForAnalytics,
      acquisition_campaign: utmCampaignForAnalytics,
      acquisition_landing_page: landingPageForAnalytics,
      first_touch_date: attribution.first_touch,
    });
    m.register({
      workspace_id: user.workspaceId,
      tier: plan,
      platform,
      utm_source: utmSourceForAnalytics,
      utm_medium: utmMediumForAnalytics,
      utm_campaign: utmCampaignForAnalytics,
    });
  }

  // Persist attribution to the workspace record on first signup only.
  // Server is first-touch-wins, so returning users posting again would
  // be a no-op — but gating here saves a network call per session.
  // The payload carries per-field nulls for missing cookie fields so
  // the server never persists synthetic "direct/none" data.
  if (user.workspaceId && user.firstLoginPending) {
    postAttributionOnce(user.id, user.workspaceId, platform, attribution);
  }
}

export function getWorkspaceId(): string {
  return _currentWorkspaceId;
}

export function wsLocalKey(key: string): string {
  return _currentWorkspaceId ? `${key}:${_currentWorkspaceId}` : key;
}

export function resetUser() {
  const m = mp();
  if (!m) return;
  m.reset();
}

export function trackEvent(name: string, properties?: Record<string, unknown>) {
  const m = mp();
  if (m) {
    m.track(name, properties);
  }

  const gaParams: Record<string, unknown> = {};
  if (properties) {
    if (properties.category) gaParams.event_category = properties.category;
    if (properties.label) gaParams.event_label = properties.label;
    if (properties.value !== undefined) gaParams.value = properties.value;
    for (const [k, v] of Object.entries(properties)) {
      if (!["category", "label", "value"].includes(k)) {
        gaParams[k] = v;
      }
    }
  }
  ga("event", name.toLowerCase().replace(/\s+/g, "_"), gaParams);
}

export function trackPageView(pageName?: string) {
  const m = mp();
  if (m) {
    m.register({ last_active_date: new Date().toISOString() });
    m.track_pageview(pageName ? { page: pageName } : undefined);
  }
  const gaParams: Record<string, string> = {};
  if (pageName) {
    gaParams.page_title = pageName;
    gaParams.page_location = window.location.href;
  }
  ga("event", "page_view", gaParams);
}

export function setUserProfileProperties(props: Record<string, unknown>) {
  const m = mp();
  if (!m) return;
  m.people.set(props);
}

export function setUserProfileOnce(props: Record<string, unknown>) {
  const m = mp();
  if (!m) return;
  m.people.set_once(props);
}

export function incrementUserProfile(prop: string, by?: number) {
  const m = mp();
  if (!m) return;
  m.people.increment(prop, by ?? 1);
}

export function registerSuperProperties(props: Record<string, unknown>) {
  const m = mp();
  if (!m) return;
  m.register(props);
}

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
const UTM_SESSION_KEY = "octa-utm-params";

export function getUtmParams(): Record<string, string> {
  const params = new URLSearchParams(window.location.search);
  const utms: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const val = params.get(key);
    if (val) utms[key] = val;
  }

  if (Object.keys(utms).length > 0) {
    try {
      sessionStorage.setItem(UTM_SESSION_KEY, JSON.stringify(utms));
    } catch {}
    return utms;
  }

  try {
    const stored = sessionStorage.getItem(UTM_SESSION_KEY);
    if (stored) return JSON.parse(stored) as Record<string, string>;
  } catch {}

  return utms;
}

export function captureAndRegisterUtms(): Record<string, string> {
  const utms = getUtmParams();
  if (Object.keys(utms).length > 0) {
    registerSuperProperties(utms);
  }
  return utms;
}

const _visitedModulesThisSession = new Set<string>();
const _DEPTH_KEY_BASE = "octa-feature-depth-modules";
const _DEPTH_MODULES = new Set(["AR", "AP", "Contracts", "Automations", "Reports", "Sync Hub", "Financing"]);

function _depthKey(): string {
  return wsLocalKey(_DEPTH_KEY_BASE);
}

function _loadDurableModules(): Set<string> {
  try {
    const raw = localStorage.getItem(_depthKey());
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {}
  return new Set();
}

function _saveDurableModules(modules: Set<string>) {
  try {
    localStorage.setItem(_depthKey(), JSON.stringify([...modules]));
  } catch {}
}

export function trackFeatureModuleVisited(module: string) {
  if (_visitedModulesThisSession.has(module)) return;
  _visitedModulesThisSession.add(module);
  trackEvent("Feature Module Visited", { module });
  if (_DEPTH_MODULES.has(module)) {
    const durable = _loadDurableModules();
    durable.add(module);
    _saveDurableModules(durable);
    setUserProfileProperties({ feature_depth_score: durable.size });
  }
}

export function resetSessionModules() {
  _visitedModulesThisSession.clear();
}
